import { Drawer, Typography, Stack, Button, Box, IconButton } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import MapIconDrawer from './MapIconDrawer';
import { useContext } from 'react';
import { MapContext } from '../context';
import { db } from '../instant';
import { TodoType } from '../schema';
import { id } from '@instantdb/react';
import { EVENT_BOTTOM_NAV_HEIGHT } from 'src/spacing';
import { useMobileKeyboardOpen } from 'src/utils';

interface Props {
    open: boolean;
    currentTodoType: TodoType | null;
    setCurrentTodoType: (type: TodoType | null) => void;
}

export default function TodoIconDrawer(props: Props) {
    const mapContext = useContext(MapContext);
    const { sweepRect, setSweepRect, eventId, toggleTodoMode } = mapContext;
    const isKeyboardOpen = useMobileKeyboardOpen();

    // Query todo types from database
    const { isLoading, error, data } = db.useQuery({ todoTypes: {} });

    if (isLoading) {
        console.log('loading todo types...');
        return <></>;
    }
    if (error) {
        console.error('error loading todo types: ', error);
        return <div>Error fetching todo types: {error.message}</div>;
    }

    const { todoTypes } = data;
    todoTypes.sort((a, b) => (a.order as number) - (b.order as number));

    const isStageType = props.currentTodoType?.name === 'pickup-needed' || props.currentTodoType?.iconName === 'square-with-x.svg';

    const getHelpText = () => {
        if (!props.currentTodoType) {
            return 'Select the type of task';
        }

        if (isStageType) {
            return 'Click on the map to place staging area';
        }

        switch (props.currentTodoType.interactionType) {
            case 'select-existing':
                return 'Click on an existing station on the map';
            case 'place-new':
                return 'Select a station type to place';
            case 'draw-area':
                return sweepRect ? 'Confirm area or Undo' : 'Draw an area on the map for litter sweep';
            case 'simple':
                return 'Click on the map to mark location';
            default:
                return 'Select an action on the map';
        }
    };

    const showStationSelector = props.currentTodoType?.interactionType === 'place-new' && !isStageType;
    const showSweepActions = props.currentTodoType?.interactionType === 'draw-area' && sweepRect;

    const handleUndoSweep = () => {
        setSweepRect(null);
    };

    const handleSaveSweep = async () => {
        if (!sweepRect || !props.currentTodoType) return;

        const todoId = id();
        const todo = {
            id: todoId,
            completed: false,
            created_at: new Date().toISOString(),
            completed_at: new Date().toISOString(),
            notes: '',
            areaCoordinates: JSON.stringify({ bounds: sweepRect, mapNumber: mapContext.mapNumber }),
        };

        await db.transact(db.tx.todos[todoId].update(todo).link({ event: eventId }).link({ todoType: props.currentTodoType.id }));

        // reset
        setSweepRect(null);
        props.setCurrentTodoType(null);
        toggleTodoMode();
    };

    const showBackButton = showStationSelector || showSweepActions;

    return (
        <Drawer
            id="todo-drawer"
            anchor="bottom"
            variant="persistent"
            open={props.open}
            sx={{ zIndex: 1000 }}
            PaperProps={{
                sx: {
                    bottom: isKeyboardOpen ? 0 : EVENT_BOTTOM_NAV_HEIGHT,
                    transition: 'bottom 0.3s ease-in-out',
                },
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
                {showBackButton && (
                    <IconButton onClick={() => props.setCurrentTodoType(null)} sx={{ position: 'absolute', left: 8 }}>
                        <ArrowBackIcon />
                    </IconButton>
                )}
                <Typography sx={{ margin: 2, marginBottom: 0, flex: 1, textAlign: showBackButton ? 'center' : 'left' }}>
                    {getHelpText()}
                </Typography>
                <IconButton onClick={toggleTodoMode} sx={{ position: 'absolute', right: 8 }}>
                    <CloseIcon />
                </IconButton>
            </Box>

            {!props.currentTodoType && (
                <Stack direction="row" spacing={2} sx={{ padding: 2, overflowX: 'scroll' }}>
                    {todoTypes.map(todoType => (
                        <Button key={todoType.id} variant="contained" onClick={() => props.setCurrentTodoType(todoType)}>
                            {todoType.name as string}
                        </Button>
                    ))}
                </Stack>
            )}

            {showSweepActions && (
                <Stack direction="row" spacing={2} sx={{ padding: 2 }}>
                    <Button variant="outlined" onClick={handleUndoSweep} color="error">
                        Undo
                    </Button>
                    <Button variant="contained" onClick={handleSaveSweep} color="primary">
                        Confirm Sweep Area
                    </Button>
                </Stack>
            )}

            {showStationSelector && (
                <MapIconDrawer
                    open={true}
                    todo={true}
                    currentStation={mapContext.currentStation}
                    setCurrentStation={mapContext.setCurrentStation}
                />
            )}
        </Drawer>
    );
}
